import { fabric } from "fabric";
import type {
  Canvas,
  Object as FabricObject,
  IGroupOptions,
  IImageOptions,
  ITextboxOptions,
} from "fabric/fabric-impl";
import { omit } from "lodash";
import { Spinner } from "./spinner";

export class BorderedTextBox extends fabric.Group {
  id?: string;

  constructor(text: string, options?: ITextboxOptions, id?: string) {
    const textBox = new fabric.Textbox(
      text,
      omit(options ?? {}, ["angle", "left", "top", "height"]),
    );
    super([textBox], {
      left: options?.left ?? 0,
      top: options?.top ?? 0,
      angle: options?.angle ?? 0,
      height: textBox.height,
      lockScalingY: options?.lockScalingY ?? false,
      lockScalingFlip: true,
    });
    this.add(this.createBorderRect());
    this.id = id ?? new Date().getTime().toString();
  }

  setOptions(options?: ITextboxOptions & { id?: string }): void {
    const textBox = this.getTextbox();
    const textBoxOptions = omit(options ?? {}, [
      "angle",
      "left",
      "top",
      "height",
    ]);

    super.setOptions({
      left: options?.left ?? this.left,
      top: options?.top ?? this.top,
      angle: options?.angle ?? this.angle,
      width: textBox.width,
      height: textBox.height,
      lockScalingY: options?.lockScalingY ?? false,
      lockScalingFlip: true,
    });

    textBox.setOptions({
      ...textBoxOptions,
      left: -(this.width ?? 0) / 2,
      top: -(this.height ?? 0) / 2,
    });

    const borderElm = this.getBorderRect();
    if (borderElm) {
      borderElm.setOptions(this.getBorderOptions());
    }

    if (options?.id) {
      this.set("id", options.id);
    }
    this.setCoords();
  }

  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);
    const textBox = this.getTextbox();
    if (textBox.height !== this.height) {
      this.setOptions({ height: textBox.height });
    }
  }

  setText(text: string): void {
    const textBox = this.getTextbox();
    textBox.set("text", text);

    this.height = textBox.height;
  }

  getTextbox(): fabric.Textbox {
    const objects = this.getObjects("textbox") as fabric.Textbox[];
    return objects[0];
  }

  getBorderRect(): fabric.Rect {
    const objects = this.getObjects("rect") as fabric.Rect[];
    return objects[0];
  }

  createBorderRect(): fabric.Rect {
    return new fabric.Rect(this.getBorderOptions());
  }

  getBorderOptions(): fabric.IRectOptions {
    const textBox = this.getTextbox();
    const { strokeStyle, lineWidth } = textBox.data ?? {};

    const shouldRenderWithRoundCorner = !lineWidth && textBox.selectable;

    const anchorPoint = textBox.getCenterPoint();
    const defaultLineWith = !textBox.selectable ? 0 : 1;
    const borderOptions = getOutlinedBoxProperties({
      anchorPoint,
      angle: 0,
      width: textBox.width,
      height: textBox.height,
      lineWidth: shouldRenderWithRoundCorner ? defaultLineWith : lineWidth,
      strokeStyle: shouldRenderWithRoundCorner ? "#e9e9e9" : strokeStyle,
    });

    borderOptions.rx = shouldRenderWithRoundCorner ? 5 : 0;
    borderOptions.ry = shouldRenderWithRoundCorner ? 5 : 0;
    return borderOptions;
  }
}

export const ImageLoaderDelay = 700;

export class BorderedImage extends fabric.Image {
  private readonly _element: HTMLImageElement | undefined;
  private readonly _spinner: Spinner;
  private loading: boolean = false;
  private loadingTimeout: number | undefined;
  private loadPromise: Promise<void> = Promise.resolve();

  constructor(element: HTMLImageElement, options?: IImageOptions) {
    super(element, options);
    this._element = element;
    this._spinner = new Spinner();
  }

  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);

    const { top = 0, left = 0, width = 1, height = 1 } = this;

    this.spinner.set({ left, top, width, height });

    if (this.loading) {
      this.spinner.render(ctx);
    }

    renderBoxAroundElement(this, ctx);
  }

  // Override fabric.Image._renderFill — the default implementation caps the
  // destination rect by the element's natural size (`min(this.width, naturalWidth)`),
  // so the image cannot be drawn larger than its natural dimensions. We draw
  // exactly into this.width × this.height so the selection frame and the pixels match.
  _renderFill(ctx: CanvasRenderingContext2D): void {
    const element = this._element;
    if (!element) {
      return;
    }

    const elWidth = element.naturalWidth || element.width;
    const elHeight = element.naturalHeight || element.height;
    if (!elWidth || !elHeight) {
      return;
    }

    const w = this.width ?? 1;
    const h = this.height ?? 1;

    ctx.drawImage(element, 0, 0, elWidth, elHeight, -w / 2, -h / 2, w, h);
  }

  // Override fabric.Image._toSVG — the default implementation writes
  // width="element.width || element.naturalWidth" / height="element.height"
  // on the emitted <image>, ignoring this.width/this.height. With
  // scaleX=scaleY=1 (see loadImage) that pinned the PDF-rendered image to
  // the source's natural size, the same root-cause bug we fixed on canvas
  // via _renderFill. We emit this.width/this.height so the SVG renderer
  // scales the embedded bitmap to the visual box.
  //
  // Fabric's runtime contract returns string[] (consumed by
  // _createBaseSVGMarkup via Array.indexOf('COMMON_PARTS')) but the @types
  // declare string — hence the cast.
  _toSVG(): string {
    const self = this as unknown as {
      _element: HTMLImageElement | undefined;
      imageSmoothing?: boolean;
      getSvgSrc: (filtered?: boolean) => string;
    };
    const element = self._element;
    if (!element) {
      return [] as unknown as string;
    }
    const x = -(this.width ?? 0) / 2;
    const y = -(this.height ?? 0) / 2;
    const imageRendering = self.imageSmoothing
      ? ""
      : '" image-rendering="optimizeSpeed';
    const markup: string[] = [
      "\t<image ",
      "COMMON_PARTS",
      'xlink:href="',
      self.getSvgSrc(true),
      '" x="',
      String(x),
      '" y="',
      String(y),
      '" width="',
      String(this.width),
      '" height="',
      String(this.height),
      imageRendering,
      '"></image>\n',
    ];
    return markup as unknown as string;
  }

  get spinner(): Spinner {
    return this._spinner;
  }

  loadImage(url: string): void {
    if (!url) {
      return;
    }

    if (!this.loading) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = window.setTimeout(
        () => this.showLoader(),
        ImageLoaderDelay,
      );
    }

    const previousLoad = this.loadPromise;
    const currentLoad = new Promise<void>((resolve) => {
      fabric.Image.fromURL(
        url,
        (img) => {
          const { width = 1, height = 1 } = this;
          const loadedElement = img?.getElement() as
            | HTMLImageElement
            | undefined;

          if (!loadedElement || !img.width || !img.height) {
            this.setElement(new Image(150, 150), { width, height });
          } else {
            this.setElement(loadedElement, {
              width,
              height,
              scaleX: 1,
              scaleY: 1,
            });
          }

          this.canvas?.requestRenderAll();
          this.setCoords();
          this.hideLoader();
          resolve();
        },
        { crossOrigin: "anonymous" },
      );
    });
    this.loadPromise = Promise.all([previousLoad, currentLoad]).then(
      () => undefined,
    );
  }

  whenLoaded(): Promise<void> {
    return this.loadPromise;
  }

  showLoader(): void {
    if (!this.canvas || this.loading) {
      return;
    }

    this.loading = true;
    this.canvas.add(this.spinner);
    this.spinner.startAnimation();
    this.canvas?.requestRenderAll();
  }

  hideLoader(): void {
    clearTimeout(this.loadingTimeout);
    this.loading = false;
    this.canvas?.remove(this.spinner);
    this.spinner.stopAnimation();
    this.canvas?.requestRenderAll();
  }

  isLoading(): boolean {
    return this.loading;
  }

  /**
   * We need to convert the image to Base64 when converting it to SVG so it doesn't reference an external URL.
   */
  getSvgSrc(): string {
    return this.toDataURLforSVG();
  }

  toDataURLforSVG(): string {
    const el = fabric.util.createCanvasElement();
    if (!this._element) {
      return "";
    }

    el.width = this._element.naturalWidth || this._element.width;
    el.height = this._element.naturalHeight || this._element.height;
    el.getContext("2d")?.drawImage(this._element, 0, 0);
    return el.toDataURL();
  }
}

export class BorderedText extends fabric.Text {
  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);
    renderBoxAroundElement(this, ctx);
  }
}

export class BorderedGroup extends fabric.Group {
  id: string;

  constructor(
    objects?: FabricObject[],
    options?: IGroupOptions,
    isAlreadyGrouped?: boolean,
    id?: string,
  ) {
    super(objects, options, isAlreadyGrouped);
    this.id = id ?? new Date().getTime().toString();
  }

  render(ctx: CanvasRenderingContext2D): void {
    super.render(ctx);
    renderBoxAroundElement(this, ctx, this.id);
  }
}

export const renderBoxAroundElement = (
  obj: fabric.Object,
  ctx: CanvasRenderingContext2D,
  objectId: string = "",
): void => {
  // The outlined box is a separate canvas object, so an invisible element has to
  // take it down explicitly — nothing else ever removes it.
  if (obj.visible === false) {
    removeOutlinedBox(obj.canvas, objectId);
    return;
  }

  // Renders a border around the element. If the element does not specify border styles,
  // render a rounded, light-gray border instead
  const { strokeStyle, lineWidth } = obj.data ?? {};
  const {
    left: groupLeftPos = 0,
    top: groupTopPos = 0,
    height: groupHeight = 0,
    width: groupWidth = 0,
  } = obj.group ?? {};
  const shouldRenderWithRoundCorner = !lineWidth && obj.selectable;
  const shouldRenderWithWidth = !!lineWidth;

  if (shouldRenderWithRoundCorner) {
    removeOutlinedBox(obj.canvas, objectId);

    const anchorPoint = obj.getPointByOrigin("left", "top");
    anchorPoint.x = anchorPoint.x + groupLeftPos + groupWidth / 2;
    anchorPoint.y = anchorPoint.y + groupTopPos + groupHeight / 2;

    renderShadedBox(
      ctx,
      anchorPoint,
      obj.angle,
      obj.getScaledWidth(),
      obj.getScaledHeight(),
    );

    obj.setCoords();
  }

  if (shouldRenderWithWidth) {
    const anchorPoint = obj.getCenterPoint();
    anchorPoint.x = anchorPoint.x + groupLeftPos + groupWidth / 2;
    anchorPoint.y = anchorPoint.y + groupTopPos + groupHeight / 2;

    renderOutlinedBox({
      canvas: obj.canvas,
      objectId,
      anchorPoint,
      angle: obj.angle,
      width: obj.width,
      height: obj.height,
      lineWidth,
      strokeStyle,
    });
  }
};

export const renderShadedBox = (
  ctx: CanvasRenderingContext2D,
  anchorPoint: fabric.Point,
  angle: number | undefined,
  width: number,
  height: number,
): void => {
  // Save context before its transformations
  ctx.save();

  const normalizedAngle = Math.floor((angle ?? 0) % 360);
  ctx.translate(anchorPoint.x, anchorPoint.y);
  ctx.rotate(normalizedAngle * (Math.PI / 180));
  ctx.translate(-anchorPoint.x, -anchorPoint.y);

  // Start drawing the element's border
  ctx.beginPath();

  const padding = 1;
  const x = anchorPoint.x - padding;
  const y = anchorPoint.y - padding;
  const widthWithPadding = width + 2 * padding;
  const heightWithPadding = height + 2 * padding;
  ctx.lineWidth = 1;
  ctx.strokeStyle = "#e9e9e9";

  if (ctx.roundRect) {
    ctx.roundRect(x, y, widthWithPadding, heightWithPadding, [4]);
  } else {
    ctx.rect(x, y, widthWithPadding, heightWithPadding);
  }

  // Finishes drawing the border
  ctx.closePath();
  ctx.stroke();

  // Restore context to its original state to reset transformations made
  ctx.resetTransform();
  ctx.restore();
};

export const getOutlinedBox = (
  canvas: Canvas | undefined,
  objectId: string,
): fabric.Rect | undefined => {
  if (!canvas) {
    return;
  }

  const rectId = getOutlinedBoxId(objectId);

  return canvas.getObjects().find((obj) => {
    if ("id" in obj) {
      return obj.id === rectId;
    }
    return false;
  });
};

interface OutlinedBoxProperties {
  anchorPoint: fabric.Point;
  angle: number | undefined;
  width: number | undefined;
  height: number | undefined;
  lineWidth: number | undefined;
  strokeStyle: string | undefined;
}

const getOutlinedBoxProperties = ({
  anchorPoint,
  angle,
  width = 1,
  height = 1,
  lineWidth = 0,
  strokeStyle = "#000",
}: OutlinedBoxProperties): fabric.IRectOptions => {
  const padding = 1;

  const normalizedAngle = Math.floor((angle ?? 0) % 360);

  return {
    angle: normalizedAngle,
    left: anchorPoint.x,
    top: anchorPoint.y,
    width: width + lineWidth + padding,
    height: height + lineWidth + padding,
    fill: "transparent",
    stroke: strokeStyle,
    strokeWidth: lineWidth,
    selectable: false,
    originX: "center",
    originY: "center",
    evented: false,
  };
};

interface OutlinedBox {
  canvas: Canvas | undefined;
  objectId: string;
  anchorPoint: fabric.Point;
  angle: number | undefined;
  width: number | undefined;
  height: number | undefined;
  lineWidth: number | undefined;
  strokeStyle: string | undefined;
}

export const renderOutlinedBox = ({
  canvas,
  objectId,
  anchorPoint,
  angle,
  width,
  height,
  lineWidth,
  strokeStyle,
}: OutlinedBox): fabric.Rect | undefined => {
  if (!canvas) {
    return;
  }

  const rectProperties = getOutlinedBoxProperties({
    anchorPoint,
    angle,
    width,
    height,
    lineWidth,
    strokeStyle,
  });

  const currentRect = getOutlinedBox(canvas, objectId);
  if (currentRect) {
    currentRect.set(rectProperties);
    return;
  }

  const rectId = getOutlinedBoxId(objectId);
  const newCurrentRect = new fabric.Rect(rectProperties);
  newCurrentRect.setOptions({ id: rectId });
  canvas.add(newCurrentRect);
};

export const removeOutlinedBox = (
  canvas: Canvas | undefined,
  objectId: string,
): void => {
  if (!canvas || !objectId) {
    return;
  }

  const currentRect = getOutlinedBox(canvas, objectId);
  if (currentRect) {
    canvas.remove(currentRect);
  }
};

export const getOutlinedBoxId = (objectId: string): string => {
  return `${objectId}-rect`;
};
